/**
 * PRO Hairstylist Dashboard - Premium Edition
 * 
 * Elegant analytics dashboard for PRO hairstylists
 * Premium beauty-tech aesthetic with soft pastel colors
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import useHairstylistStats from '../hooks/useHairstylistStats';
import StarRating from '../components/StarRating';
import styles from './HairstylistDashboardPremium.module.css';
import instagramIcon from '../assets/images/logo_instagram.png';
import whatsappIcon from '../assets/images/logo_whatsapp.png';
import tiktokIcon from '../assets/images/tiktok_logo.png';
import linkIcon from '../assets/images/logo_personal_link.png';

// Premium Icons - Heroicons outline style, 24px, stroke 1.5, color #A17BB6
const EyeIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#A17BB6" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const UserIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#A17BB6" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
  </svg>
);

const HeartIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#A17BB6" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
  </svg>
);

const ImageIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#A17BB6" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
  </svg>
);

const LightbulbIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#A17BB6" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m4.5 0a12.05 12.05 0 003.478-.397M9.75 15.75a12.05 12.05 0 01-3.478-.397M9.75 15.75V18m0 0h3.75m-3.75 0h-3.75m0 0v-1.5m0 1.5h-3.75m3.75 0v-1.5m0 0h3.75m-3.75 0h-3.75" />
  </svg>
);


const RefreshIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
  </svg>
);

// Premium Stat Card Component
const StatCard = ({ icon: Icon, value, label, description, iconColor }) => (
  <div className={styles.statCard}>
    <div className={styles.statIcon} style={{ color: iconColor }}>
      <Icon />
    </div>
    <div className={styles.statContent}>
      <div className={styles.statValue}>{value}</div>
      <div className={styles.statLabel}>{label}</div>
      {description && <div className={styles.statDescription}>{description}</div>}
    </div>
  </div>
);

// Contact Card Component (smaller)
const ContactCard = ({ iconSrc, value, label, description, color }) => (
  <div className={styles.contactCard}>
    <div className={styles.contactIcon} style={{ backgroundColor: `${color}15` }}>
      <img src={iconSrc} alt={label} className={styles.contactIconImage} />
    </div>
    <div className={styles.contactValue}>{value}</div>
    <div className={styles.contactLabel}>{label}</div>
    {description && <div className={styles.contactDescription}>{description}</div>}
  </div>
);

// Summary Card Component
const SummaryCard = ({ timeRange, stats }) => {
  const getTimeRangeLabel = () => {
    if (timeRange === '1day') return 'dernières 24 heures';
    if (timeRange === '7days') return '7 derniers jours';
    if (timeRange === '30days') return '30 derniers jours';
    return 'toutes les périodes';
  };

  return (
    <div className={styles.summaryCard}>
      <h3 className={styles.summaryTitle}>Résumé des {getTimeRangeLabel()}</h3>
      <ul className={styles.summaryList}>
        <li>{(stats?.profileOpens || 0)} ouvertures de profil</li>
        <li>{(stats?.searchViews || 0)} apparitions dans la recherche</li>
        <li>{(stats?.currentFavorites || 0)} favoris</li>
      </ul>
    </div>
  );
};

// Insight Card Component
const InsightCard = ({ insight }) => (
  <div className={styles.insightCard}>
    <div className={styles.insightIcon}>
      <LightbulbIcon />
    </div>
    <div className={styles.insightContent}>
      <p className={styles.insightText}>{insight}</p>
    </div>
  </div>
);

function HairstylistDashboardPremium() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [hairstylistProfile, setHairstylistProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7days');

  // Fetch hairstylist profile
  useEffect(() => {
    const fetchProfile = async () => {
      if (!currentUser) {
        navigate('/');
        return;
      }

      try {
        const hairstylistsRef = collection(db, 'hairstylists');
        const q = query(hairstylistsRef, where('userId', '==', currentUser.uid));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const hairstylistDoc = querySnapshot.docs[0];
          const profileData = {
            id: hairstylistDoc.id,
            ...hairstylistDoc.data(),
          };
          
          setHairstylistProfile(profileData);
          
          if (profileData.accountType !== 'PRO') {
          }
        } else {
          navigate('/');
        }
      } catch (error) {
        console.error('[Dashboard] Error fetching profile:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [currentUser, navigate]);

  // Fetch stats using custom hook
  const { stats, loading: statsLoading, error: statsError, refetch } = useHairstylistStats(
    hairstylistProfile?.id,
    timeRange
  );

  // Get stat value - stats are already filtered by timeRange in the hook
  const getStatValue = (value) => {
    return value || 0;
  };

  if (loading || !hairstylistProfile) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>Chargement...</div>
      </div>
    );
  }

  // Check if user is PRO
  const isPro = hairstylistProfile.accountType === 'PRO';

  if (!isPro) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <div className={styles.upgradePrompt}>
            <h2>Tableau de bord PRO</h2>
            <p>Cette fonctionnalité est réservée aux comptes PRO.</p>
            <button
              type="button"
              className={styles.upgradeButton}
              onClick={() => navigate('/signup/payment')}
            >
              Passer PRO maintenant
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* Premium Header */}
        <div className={styles.header}>
          <h1 className={styles.title}>
            Mon tableau de bord <span className={styles.proBadge}>PRO</span>
          </h1>
          <p className={styles.subtitle}>Vos performances, en toute clarté.</p>
        </div>

        {/* Summary Card */}
        {stats && !statsLoading && (
          <SummaryCard timeRange={timeRange} stats={stats} />
        )}

        {/* Time Range Selector */}
        <div className={styles.timeRangeSelector}>
          <button
            type="button"
            className={`${styles.timeRangeButton} ${timeRange === '1day' ? styles.active : ''}`}
            onClick={() => setTimeRange('1day')}
          >
            1 jour
          </button>
          <button
            type="button"
            className={`${styles.timeRangeButton} ${timeRange === '7days' ? styles.active : ''}`}
            onClick={() => setTimeRange('7days')}
          >
            7 jours
          </button>
          <button
            type="button"
            className={`${styles.timeRangeButton} ${timeRange === '30days' ? styles.active : ''}`}
            onClick={() => setTimeRange('30days')}
          >
            30 jours
          </button>
          <button
            type="button"
            className={`${styles.timeRangeButton} ${timeRange === 'lifetime' ? styles.active : ''}`}
            onClick={() => setTimeRange('lifetime')}
          >
            Tout
          </button>
        </div>

        {/* Insight Card */}
        <InsightCard insight="Votre profil gagne en visibilité. Ajouter 2–3 nouvelles photos peut augmenter vos interactions." />

        {statsLoading ? (
          <div className={styles.loading}>Chargement des statistiques...</div>
        ) : statsError ? (
          <div className={styles.error}>Erreur: {statsError}</div>
        ) : !stats ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyStateIcon}>📊</div>
            <h3 className={styles.emptyStateTitle}>Aucune donnée pour le moment</h3>
            <p className={styles.emptyStateText}>
              Les statistiques apparaîtront dès que des utilisateurs interagiront avec votre profil.
              <br />
              <br />
              Partagez votre profil pour commencer à collecter des données !
            </p>
          </div>
        ) : (
          <>
            {/* Section: Visibilité */}
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <EyeIcon />
                <div>
                  <h2 className={styles.sectionTitle}>Visibilité</h2>
                  <p className={styles.sectionDescription}>Comment les clientes vous découvrent.</p>
                </div>
              </div>
              <div className={styles.statsGrid}>
                <StatCard
                  icon={EyeIcon}
                  value={getStatValue(stats?.searchViews)}
                  label="Apparitions dans la recherche"
                  description="Nombre de fois où votre profil a été affiché dans les résultats de recherche."
                  iconColor="#A17BB6"
                />
                <StatCard
                  icon={UserIcon}
                  value={getStatValue(stats?.profileOpens)}
                  label="Ouvertures du profil"
                  description="Clientes ayant consulté votre page détaillée."
                  iconColor="#A17BB6"
                />
              </div>
            </section>

            {/* Section: Contacts */}
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#A17BB6" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
                </svg>
                <div>
                  <h2 className={styles.sectionTitle}>Contacts</h2>
                  <p className={styles.sectionDescription}>Où vos clientes choisissent de vous écrire.</p>
                </div>
              </div>
              <div className={styles.contactGrid}>
                <ContactCard
                  iconSrc={instagramIcon}
                  value={getStatValue(stats?.instagramClicks)}
                  label="Instagram"
                  description="Redirections vers votre profil Instagram."
                  color="#E1306C"
                />
                <ContactCard
                  iconSrc={whatsappIcon}
                  value={getStatValue(stats?.whatsappClicks)}
                  label="WhatsApp"
                  description="Conversations potentiellement initiées."
                  color="#25D366"
                />
                <ContactCard
                  iconSrc={tiktokIcon}
                  value={getStatValue(stats?.tiktokClicks)}
                  label="TikTok"
                  description="Visites vers votre contenu vidéo."
                  color="#000000"
                />
                <ContactCard
                  iconSrc={linkIcon}
                  value={getStatValue(stats?.linkClicks)}
                  label="Lien externe"
                  description="Visites vers votre site web ou lien personnel."
                  color="#A17BB6"
                />
              </div>
            </section>

            {/* Section: Engagement */}
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <HeartIcon />
                <div>
                  <h2 className={styles.sectionTitle}>Engagement</h2>
                  <p className={styles.sectionDescription}>Interactions directes avec votre profil.</p>
                </div>
              </div>
              <div className={styles.statsGrid}>
                <StatCard
                  icon={HeartIcon}
                  value={getStatValue(stats?.currentFavorites)}
                  label="Favoris"
                  description="Clientes ayant sauvegardé votre profil."
                  iconColor="#A17BB6"
                />
                <StatCard
                  icon={ImageIcon}
                  value={getStatValue(stats?.galleryOpens)}
                  label="Ouvertures galerie"
                  description="Consultations de vos photos."
                  iconColor="#A17BB6"
                />
              </div>
            </section>

            {/* Section: Avis */}
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#A17BB6" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
                </svg>
                <div>
                  <h2 className={styles.sectionTitle}>Avis</h2>
                  <p className={styles.sectionDescription}>Réputation & confiance.</p>
                </div>
              </div>
              <div className={styles.reviewsCard}>
                <div className={styles.ratingDisplay}>
                  <div className={styles.ratingValue}>
                    {stats?.averageRating ? stats.averageRating.toFixed(1) : '0.0'}
                  </div>
                  <div className={styles.starsContainer}>
                    <StarRating rating={stats?.averageRating || 0} reviewCount={0} />
                  </div>
                  <div className={styles.reviewCount}>
                    {stats?.totalReviews || 0} {stats?.totalReviews === 1 ? 'avis' : 'avis'}
                  </div>
                  <p className={styles.reviewNote}>Basé sur les retours authentiques des clientes.</p>
                </div>
              </div>
            </section>

            {/* Footer */}
            <div className={styles.footer}>
              <p className={styles.footerText}>
                Les données sont mises à jour en temps réel pour vous aider à optimiser votre visibilité.
              </p>
              <button
                type="button"
                className={styles.refreshButton}
                onClick={refetch}
              >
                <RefreshIcon />
                <span>Actualiser les données</span>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default HairstylistDashboardPremium;
